import React, { useState } from 'react';
import { IndianRupee, Wallet } from 'lucide-react';
import { AppButton, cn } from '../../../ui';
import FeePayFlow from '../../../QrPayments/FeePayFlow';
import useStudentPendingFees from '../../../../hooks/useStudentPendingFees';
import { convertToIndianCurrency } from '../../../../utils/common';
import { CardLink } from '../shared/DashCard';

const shortDate = (date) => new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const emiLine = ({ label, dueDate, overdue }) => {
  if (!dueDate) return label;
  return `${label} · ${overdue ? 'overdue since' : 'due'} ${shortDate(dueDate)}`;
};

const payLabel = (item, gateway) => {
  if (!gateway) return 'Pay by QR';
  return item.nextEmi ? 'Pay EMI' : 'Pay Now';
};

const FeeRow = ({ item, canPay, gateway, onPay }) => (
  <li className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2.5">
    <div className="tw-min-w-0 tw-flex-1">
      <div className="tw-truncate tw-text-[0.8125rem] tw-font-semibold tw-text-content">{item.courseName}</div>
      {item.batchName && <div className="tw-truncate tw-text-[0.6875rem] tw-text-content-subtle">{item.batchName}</div>}
      {item.nextEmi && (
        <div className={cn('tw-mt-0.5 tw-truncate tw-text-xs tw-font-semibold', item.nextEmi.overdue ? 'tw-text-danger-fg' : 'tw-text-primary')}>
          {emiLine(item.nextEmi)}
        </div>
      )}
      <div className="tw-mt-0.5 tw-text-xs tw-font-medium tw-text-warning-fg">
        {convertToIndianCurrency(item.nextEmi ? item.nextEmi.balance : item.pendingFees)}
        {item.nextEmi && (
          <span className="tw-text-[0.6875rem] tw-text-content-subtle"> of {convertToIndianCurrency(item.pendingFees)} total</span>
        )}
      </div>
    </div>
    {canPay && (
      <AppButton variant="success" size="sm" className="tw-shrink-0" onClick={() => onPay(item)}>
        <IndianRupee size={16} />
        {payLabel(item, gateway)}
      </AppButton>
    )}
  </li>
);

/**
 * What the student owes, on every screen size. `FeePayFlow` is the one way in to
 * paying, and `canPay` counts the gateway or the UPI QR. Without either the debt is
 * still shown, with no button.
 */
const FeesDue = ({ onPaid }) => {
  const fees = useStudentPendingFees();
  const [payTarget, setPayTarget] = useState(null);

  if (!fees.hasPending) return null;

  const settled = () => {
    fees.refresh();
    onPaid?.();
  };

  const startPaying = (item) => setPayTarget({
    courseAssignmentId: item.courseAssignmentId || null,
    courseName: item.courseName,
    pending: item.pendingFees,
    defaultAmount: item.nextEmi?.balance ?? null,
  });

  return (
    <>
      <section
        aria-label="Fees due"
        className="tw-rounded-lg tw-border tw-border-solid tw-border-success/20 tw-bg-gradient-to-br tw-from-success-soft tw-to-surface-raised tw-to-60% tw-p-4 tw-shadow-card page-sm:tw-px-5"
      >
        <div className="tw-mb-3 tw-flex tw-items-center tw-gap-3">
          <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-success/20 tw-bg-surface-raised tw-text-success" aria-hidden="true">
            <Wallet size={18} />
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <h2 className="tw-m-0 tw-text-[0.9375rem] tw-font-medium tw-text-content">Fees Due</h2>
            <p className="tw-m-0 tw-text-xs tw-text-content-muted">
              Total pending: <strong className="tw-font-semibold tw-text-success-fg">{convertToIndianCurrency(fees.totalPending)}</strong>
            </p>
          </div>
          <CardLink to="/s/fees">View all</CardLink>
        </div>
        <ul className="tw-m-0 tw-grid tw-list-none tw-gap-2 tw-p-0 page-md:tw-grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
          {fees.items.map((item) => (
            <FeeRow
              key={item.assignmentId || item.courseName}
              item={item}
              canPay={fees.canPay}
              gateway={fees.paymentEnabled}
              onPay={startPaying}
            />
          ))}
        </ul>
      </section>

      <FeePayFlow
        target={payTarget}
        onClose={() => setPayTarget(null)}
        student={{ _id: fees.studentId, pendingFees: payTarget?.pending ?? fees.totalPending }}
        paymentEnabled={fees.paymentEnabled}
        qrConfig={fees.qrConfig}
        onPaid={settled}
        onQrSubmitted={settled}
      />
    </>
  );
};

export default FeesDue;
